import { Dot, Split } from 'lucide-react';
import { ProjectTag } from '../scope/ScopeBar';
import type { KbRun } from '../../lib/api';
import { formatChars, isRunLive } from '../../lib/runs';
import { cn } from '../../lib/utils';

/**
 * Whether a run's destination was pinned by whoever sent the content, or chosen by the ingestor —
 * and, when it was chosen, which projects it chose.
 *
 * Shared rather than local to the review queue because the same ambiguity bites on every surface
 * that lists runs: `KbRun.projectId` is the scope the content was *read from*, which for a routed
 * run is not where it was written, and a row showing that one name reads as though it were.
 */

/** How many destination tags a row shows before it starts counting the rest. */
const ROW_DESTINATIONS = 2;

/**
 * How this run's destination was decided, as one glyph.
 *
 * Two glyphs rather than a word because it sits in a dense row, and the distinction is binary: the
 * content arrived pinned to a project, or the ingestor was handed it with nowhere to put it and
 * chose. Shared with the review drawer's header and a project's Activity section, so the vocabulary
 * is learnt once wherever runs are listed.
 *
 * The sentence explaining it belongs to whatever wraps this — an svg takes no `title` attribute, and
 * both callers already put one on the element holding the glyph and the project names together,
 * which is the region a reader would hover anyway.
 */
export function PlacementMark({ triage }: { triage: boolean }) {
  return triage ? (
    // Turned a quarter clockwise so the fork runs left to right: the arrival on the left, the
    // destinations it was divided between on the right, which is the order the row reads in.
    <Split size={10} className="flex-shrink-0 text-orange-500 rotate-90" aria-label="Auto-routed" />
  ) : (
    // A single point: the content arrived at its destination with no branching to draw — the
    // antonym of the fork. Drawn larger than the fork because lucide's Dot is a 1-unit circle in a
    // 24-unit box, and at the fork's 10px it would be a sub-pixel speck; the negative margin keeps
    // the two glyphs occupying the same width in a row.
    <Dot size={18} strokeWidth={4} className="flex-shrink-0 -mx-1 text-ink-faint" aria-label="Pinned" />
  );
}

/**
 * Where the run wrote, and whether it chose.
 *
 * The row used to show a single project tag either way — `run.projectId` resolved through the tree —
 * which is right for a pinned run and actively misleading for a routed one: there `projectId` is only
 * the scope the content was *read* from, almost always the organization root, and the projects it
 * actually wrote to appeared nowhere outside the drawer. A reviewer scanning the list could not tell
 * "this went where the channel says it goes" from "an agent decided this", which are different things
 * to be sceptical about.
 *
 * Names come off the run's own destinations rather than out of the project tree, so a project the
 * reader is not a member of still reads as a name instead of dropping out of a list of three.
 */
export function RunPlacement({ run, projectName }: { run: KbRun; projectName: string | null }) {
  const scope = projectName ?? 'the organization';

  if (!run.triage) {
    // A pinned run records the project it was pinned to as its one destination, so that name stands
    // in when the tree lookup finds nothing — which is what the row did before, showing no project
    // at all rather than the one the run itself names.
    const pinned = projectName ?? run.destinations?.[0]?.projectName ?? null;
    return (
      <span
        className="flex items-center gap-1 flex-shrink-0"
        title={`Pinned to ${pinned ?? 'one project'} — this content arrived with its destination already set, so no routing was done`}
      >
        <PlacementMark triage={false} />
        <ProjectTag name={pinned} />
      </span>
    );
  }

  const destinations = run.destinations ?? [];
  // Widest first, because the list is capped: truncating by share drops the projects that received
  // least, where the stored order would drop whichever happened to be decided last — an accident of
  // the agent's reading order rather than anything about the content.
  const ordered = [...destinations].sort((a, b) => b.chars - a.chars);
  const shown = ordered.slice(0, ROW_DESTINATIONS);
  const rest = ordered.length - shown.length;
  const live = isRunLive(run.status);

  const title =
    ordered.length > 0
      ? `Auto-routed — read from ${scope}, written to:\n${ordered
          .map((d) => `${d.projectName}${d.error ? ' (not written)' : d.fellBack ? ' (unclaimed)' : ''} · ${formatChars(d.chars)}`)
          .join('\n')}`
      : live
        ? `Auto-routed — read from ${scope}; the ingestor is still deciding where this belongs`
        : `Auto-routed — read from ${scope}; nothing was placed`;

  return (
    <span className="flex items-center gap-1 flex-shrink-0" title={title}>
      <PlacementMark triage />
      {shown.map((destination) => (
        <ProjectTag
          key={destination.projectId}
          name={destination.projectName}
          className={cn(
            'max-w-[8rem] truncate',
            // A destination that threw is the one thing on this row worth noticing without opening
            // it: the run reads as done and that project's tree was never touched.
            destination.error
              ? 'bg-red-500/10 text-red-700 dark:text-red-400'
              : destination.fellBack && 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
          )}
        />
      ))}
      {rest > 0 && <span className="text-[10px] text-ink-faint">+{rest}</span>}
      {ordered.length === 0 && (
        <span className="text-[10px] text-ink-faint">{live ? 'deciding…' : 'nothing placed'}</span>
      )}
    </span>
  );
}
